/**
 * Modal Bridge - Event-based system to trigger modals from within Konva stage
 *
 * This bridge solves the problem of React Context not being available inside Konva stages
 * and the inability to render React components outside the canvas from within it.
 *
 * Uses a global event system to communicate between Konva components and the modal system.
 */

import { ModalComponent } from "./dynamic-modal";

export const MODAL_REQUEST_EVENT = "workcanvas:modal-request";

export interface ModalRequest<P = any, T = any, AnyProps = any> {
  component: ModalComponent<P, T, AnyProps>;
  onComplete?: (result: P) => void;
  props?: AnyProps;
}

/**
 * Request a modal to be shown from anywhere in the application,
 * including from within Konva stages where React Context is not available.
 */
function requestModal<P, T, AnyProps>(request: ModalRequest<P, T, AnyProps>) {
  window.dispatchEvent(new CustomEvent(MODAL_REQUEST_EVENT, { detail: request }));
}

/**
 * Convenience function to request a modal with just component and props
 */
export function showModalFromKonva<P, T, AnyProps>(
  component: ModalComponent<P, T, AnyProps>,
  props?: AnyProps,
  onComplete?: (result: P) => void
) {
  requestModal({
    component,
    props,
    onComplete,
  });
}
